import type { ElementSize, TableShape } from "../../api/restaurantSchema";
import {
  CHAIR_DEPTH,
  CHAIR_WIDTH,
  DESIGN_UNIT,
  tableChairs,
  tableDimensions,
  tableScreenExtents,
} from "./geometry";
import { CheckIcon, ClockIcon, PersonIcon } from "./schemaIcons";
import { fitBadge } from "./badgeFit";
import { BLOCKS, blockDepthPx, blockShadow } from "./blockDepth";
import { tableNameFontSize } from "./nameFit";
import { useSchemaFonts } from "./useSchemaFonts";
import BusyRing from "../BusyRing";
import SelectionHandles from "./SelectionHandles";
import {
  BADGE_TONE,
  NAME_TONE,
  plateClass,
  plateRing,
  plateSideColor,
  type TableStatus,
} from "./tableTones";

// Страната на плота е най-много толкова от по-късата му страна — малката маса
// иначе изглежда като цилиндър.
const MAX_DEPTH_SHARE = 0.09;

/**
 * Маса отгоре по дизайна: плот-блокче (страна + сянка върху пода), столове,
 * голям номер и места с иконка на човек. Размерите са логически единици ×
 * `scale` — известни чак по време на изпълнение, затова са inline style
 * (изключение (1) от правило 3). Номерът се завърта обратно, за да се чете
 * право при всяко завъртане на масата. Ползва се в редактора, в живото превю
 * на „Нова маса" и (със `status`) в модулите, които показват статуси
 * (резервации): тон на плота, надпис под номера, чип над масата, червена
 * точка, ореол, „работи се" (`busy` — приглушена маса с лента и спинер),
 * извън фокуса (`faded` — само приглушена, тонът остава), процентът по
 * сметката горе вдясно (`discount` — цветовете на баджа отстъпка в списъка).
 */
const TableView = ({
  name,
  shape,
  seats,
  size,
  rotation,
  scale,
  selected = false,
  status,
  flat = false,
}: {
  name: string;
  shape: TableShape;
  seats: number;
  size: ElementSize;
  rotation: number;
  scale: number;
  // Избор в РЕДАКТОРА (кехлибарен контур + ъглови маркери).
  selected?: boolean;
  status?: TableStatus;
  // Без страна и сянка — за сцена, която сама повдига масата (3D макетът).
  flat?: boolean;
}) => {
  useSchemaFonts();
  const tone = status?.tone ?? "free";
  const { width, height } = tableDimensions(shape, seats, size);
  const chairs = tableChairs(shape, seats, width, height);
  // Дизайнът е 22px номер и 12px места при 1px = DESIGN_UNIT единици; за дълго
  // име („Бар 1", „E2E1") номерът се свива, за да остане ЦЕЛИЯТ в плота.
  const designPixel = DESIGN_UNIT * scale;
  const plateDepth = flat
    ? 0
    : Math.min(blockDepthPx(BLOCKS.table.depth, designPixel), Math.min(width, height) * scale * MAX_DEPTH_SHARE);
  const chairDepth = flat ? 0 : blockDepthPx(BLOCKS.chair.depth, designPixel);
  const nameSize = tableNameFontSize({
    name,
    plateWidth: width * scale,
    isRound: shape === "round",
    maxSize: 22 * designPixel,
  });
  const seatsSize = Math.max(9, 12 * designPixel);
  const badge = status?.badge;
  const BadgeIcon = badge?.icon === "check" ? CheckIcon : badge?.icon === "clock" ? ClockIcon : null;
  const badgeLayout = fitBadge(badge, Boolean(BadgeIcon), {
    // Реалното място за реда: плотът без отстъпите (px-0.5); кръглата маса
    // е по-тясна под центъра, където стои редът.
    room: width * scale * (shape === "round" ? 0.82 : 1) - 6,
    maxSize: 10.5 * designPixel,
  });
  const isHighlighted = Boolean(status?.highlighted);
  const hasDecorations = Boolean(status?.chip || status?.alert || status?.busy || status?.discount);
  const extents = hasDecorations ? tableScreenExtents(shape, seats, size, rotation) : null;
  // Точката стои на ъгъла на плота; при кръгла маса — малко навътре, до ръба.
  const alertReach = shape === "round" ? 0.88 : 0.96;
  const alertSize = Math.max(14, 18 * designPixel);
  const busy = status?.busy;

  return (
    <div
      className={`relative font-sans ${tone === "dimmed" ? "opacity-40" : ""}`}
      style={{ width: width * scale, height: height * scale }}
    >
      {/* Самата маса — приглушена, докато върху нея се работи (`busy`) или
          когато е извън фокуса (`faded`) */}
      <div
        className={`absolute inset-0 transition-opacity duration-200 ${
          busy || status?.faded ? "opacity-40" : ""
        }`}
      >
        {chairs.map((chair, index) => (
          <span
            key={index}
            className={`absolute rounded-full border-[1.5px] border-schema-chair-edge bg-schema-chair ${
              tone === "blocked" ? "opacity-50" : ""
            }`}
            style={{
              left: chair.x * scale,
              top: chair.y * scale,
              width: CHAIR_WIDTH * scale,
              height: CHAIR_DEPTH * scale,
              transform: `translate(-50%, -50%) rotate(${chair.angle}deg)`,
              boxShadow: blockShadow({
                rotation: rotation + chair.angle,
                depth: chairDepth,
                color: BLOCKS.chair.side,
                floorShadow: false,
              }),
            }}
          />
        ))}
        <div
          className={`absolute inset-0 transition-[border-color,box-shadow,background-color] duration-200 ${plateClass(
            tone,
            isHighlighted,
            selected,
          )}`}
          style={{
            borderRadius: shape === "round" ? "50%" : 14 * designPixel,
            boxShadow: blockShadow({
              rotation,
              depth: plateDepth,
              color: plateSideColor(tone),
              ring: plateRing(tone, isHighlighted, selected),
              // Неактивната маса („заета в този час") не хвърля сянка — стои по-ниско.
              floorShadow: tone !== "blocked",
            }),
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-[3px]"
          style={{ transform: `rotate(${-rotation}deg)` }}
        >
          <div
            // Без „…": под минималния размер номерът прелива върху столовете, но се чете.
            className={`whitespace-nowrap px-0.5 font-bold leading-[1.1] tracking-[-0.4px] ${NAME_TONE[tone]}`}
            style={{ fontSize: nameSize }}
          >
            {name}
          </div>
          {badge ? (
            badgeLayout && (
              <div
                className={`flex max-w-full items-center gap-[0.25em] whitespace-nowrap px-0.5 font-bold leading-none ${BADGE_TONE[tone]}`}
                style={{ fontSize: badgeLayout.size }}
              >
                {BadgeIcon && badgeLayout.showIcon && <BadgeIcon className="h-[1em] w-[1em] shrink-0" />}
                <span className="truncate">{badgeLayout.text}</span>
              </div>
            )
          ) : (
            seatsSize >= 10 && (
              <div
                className={`flex items-center gap-[3px] font-semibold leading-none ${BADGE_TONE[tone]}`}
                style={{ fontSize: seatsSize }}
              >
                <PersonIcon className="h-[0.92em] w-[0.92em]" />
                {seats}
              </div>
            )
          )}
        </div>
        {extents && (
          // Котва в центъра, завъртяна обратно — вътре осите са екранните, така
          // чипът стои над най-горния стол, а точката — горе вдясно на плота.
          <div
            className="pointer-events-none absolute left-1/2 top-1/2 h-0 w-0"
            style={{ transform: `rotate(${-rotation}deg)` }}
          >
            {status?.chip && (
              <div
                className="absolute flex -translate-x-1/2 -translate-y-full items-center gap-[0.3em] whitespace-nowrap rounded-full border border-schema-next-edge bg-surface font-bold leading-none text-schema-reserved-ink"
                style={{
                  top: -(extents.topReach + 4 * DESIGN_UNIT) * scale,
                  fontSize: Math.max(8, 10 * designPixel),
                  padding: `${3 * designPixel}px ${6 * designPixel}px`,
                }}
              >
                <ClockIcon className="h-[1em] w-[1em] shrink-0" />
                {status.chip}
              </div>
            )}
            {status?.alert && (
              <div
                className="absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-surface bg-schema-alert font-extrabold leading-none text-white"
                style={{
                  left: extents.plateHalfWidth * alertReach * scale,
                  top: -extents.plateHalfHeight * alertReach * scale,
                  width: alertSize,
                  height: alertSize,
                  fontSize: Math.max(8, 10 * designPixel),
                }}
              >
                !
              </div>
            )}
            {status?.discount && (
              // На ъгъла като точката; има ли точка — вляво от нея.
              <div
                className="absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center whitespace-nowrap rounded-full border-2 border-surface bg-pos-disc-bg font-extrabold leading-none text-pos-disc-ink"
                style={{
                  left: extents.plateHalfWidth * alertReach * scale - (status.alert ? alertSize : 0),
                  top: -extents.plateHalfHeight * alertReach * scale,
                  minWidth: alertSize,
                  height: alertSize,
                  fontSize: Math.max(8, 10 * designPixel),
                  padding: `0 ${Math.max(3, 4 * designPixel)}px`,
                }}
              >
                {status.discount}
              </div>
            )}
          </div>
        )}
        {selected && <SelectionHandles />}
      </div>
      {busy && extents && (
        // Не приглушена, на долния ръб на плота (номерът остава видим) и
        // винаги права — котвата в центъра е завъртяна обратно, като на чипа.
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 z-10 h-0 w-0"
          style={{ transform: `rotate(${-rotation}deg)` }}
        >
          <div
            className="dk-elevated absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-[0.45em] whitespace-nowrap rounded-full border border-shade-2 bg-surface font-bold leading-none text-ink shadow-[0_2px_10px_rgba(28,25,23,0.18)]"
            style={{
              top: extents.plateHalfHeight * scale,
              fontSize: Math.max(10, 12 * designPixel),
              padding: `${Math.max(4, 6 * designPixel)}px ${Math.max(7, 10 * designPixel)}px`,
            }}
          >
            <BusyRing className="h-[1.25em] w-[1.25em]" arcClass="stroke-brand" trackClass="stroke-brand/25" />
            {busy}
          </div>
        </div>
      )}
    </div>
  );
};

export default TableView;
